import { ENTITY, SITE, SOCIAL_MEDIA_ARRAY, CONTACT_POINT, ASSETS } from './constants';
import { generateSchema } from '../config/schemas';
export { removeEmojis, extractDescription } from '../config/schemas';

// ─── Site-level schemas ───────────────────────────────

const generateOrganizationSchema = (baseUrl = SITE.url) => ({
  '@type': 'Organization',
  name: SITE.name,
  url: baseUrl,
  logo: ASSETS.logo,
  image: ASSETS.logo,
  description: SITE.description,
  sameAs: SOCIAL_MEDIA_ARRAY,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: CONTACT_POINT.type,
    ...(CONTACT_POINT.email && { email: CONTACT_POINT.email }),
    url: baseUrl,
  },
  ...(SITE.addressCountry ? {
    address: {
      '@type': 'PostalAddress',
      addressCountry: SITE.addressCountry,
      addressLocality: SITE.addressLocality,
    },
  } : {}),
  founder: {
    '@type': 'Person',
    name: SITE.name,
  },
});

const generateWebSiteSchema = (baseUrl = SITE.url) => ({
  '@type': 'WebSite',
  url: baseUrl,
  name: SITE.name,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${baseUrl}/?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
  publisher: {
    '@type': 'Organization',
    name: SITE.name,
    logo: { '@type': 'ImageObject', url: ASSETS.logo },
  },
});

// ─── Post page schema ─────────────────────────────────

export const generatePostSchema = (post, baseUrl = SITE.url) => {
  const mainSchema = generateSchema(
    ENTITY.schemaType || 'Article',
    post, ENTITY, SITE, ASSETS, baseUrl
  );

  return {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    mainEntity: mainSchema,
    publisher: generateOrganizationSchema(baseUrl),
    url: `${baseUrl}/${ENTITY.routes.detailPath}/${post.slug}/`,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ENTITY.cssSelectors.detail,
    },
    '@graph': [generateWebSiteSchema(baseUrl)],
  };
};

// ─── Collection page schema ───────────────────────────

export const generateCollectionSchema = (baseUrl = SITE.url) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${SITE.name} - ${ENTITY.labelPlural}`,
  description: SITE.description,
  url: baseUrl,
  image: ASSETS.defaultImage,
  sameAs: baseUrl,
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ENTITY.cssSelectors.collection,
  },
  publisher: generateOrganizationSchema(baseUrl),
  mainEntity: {
    '@type': 'ItemList',
    name: ENTITY.labelPlural,
    description: `Todos nuestros ${ENTITY.labelPluralLower}`,
  },
});

// ─── Breadcrumb schema ────────────────────────────────

export const generateBreadcrumbSchema = (items, baseUrl = SITE.url) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.label,
    item: `${baseUrl}${item.url}`,
    sameAs: `${baseUrl}${item.url}`,
  })),
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ENTITY.cssSelectors.breadcrumb,
  },
});

// ─── Meta tag extraction ──────────────────────────────

export const extractMetaTags = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return {
    title: doc.querySelector('title')?.textContent || '',
    description: doc.querySelector('meta[name="description"]')?.content || '',
    image: doc.querySelector('meta[property="og:image"]')?.content || '',
  };
};
